"use client";

import { ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { ChevronRight, Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

const legalLinks = [
  { href: "/legal/privacy", label: "Privacy Policy" },
  { href: "/legal/terms", label: "Terms of Service" },
  { href: "/legal/dpa", label: "Data Processing Agreement" },
];

export default function LegalLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-20">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:col-span-3 lg:block">
            <nav className="sticky top-24 space-y-2">
              <h2 className="mb-6 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Legal Information
              </h2>
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "flex items-center justify-between rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                    pathname === link.href
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  {link.label}
                  {pathname === link.href && (
                    <ChevronRight className="size-4" />
                  )}
                </Link>
              ))}
            </nav>
          </aside>

          {/* Mobile Navigation */}
          <div className="mb-8 lg:hidden">
            <Button
              variant="outline"
              className="w-full justify-between"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span>
                {legalLinks.find((l) => l.href === pathname)?.label ||
                  "Legal Menu"}
              </span>
              {isMobileMenuOpen ? (
                <X className="size-4" />
              ) : (
                <Menu className="size-4" />
              )}
            </Button>

            {isMobileMenuOpen && (
              <div className="mt-2 rounded-lg border bg-card p-2 shadow-lg">
                {legalLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      "block rounded-md px-4 py-2 text-sm transition-colors",
                      pathname === link.href
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <main className="lg:col-span-9">
            <div className="mb-8 text-sm text-muted-foreground">
              Last Updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>

            <div className="prose prose-neutral dark:prose-invert max-w-none prose-headings:font-serif prose-headings:font-bold prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
